// Fleet operator — whether this driver is allowed to see Hitch-owned vehicle
// opportunities.
//
// One switch and one sentence, so it is one `Row` inside a section rather than its own
// bare `Paper` with a heading. The section frame is what makes it read as part of the
// driver record instead of as a stray toggle floating between two cards.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Switch } from '../../../ui/core';
import { CheckCircleIcon } from '../../../ui/icons';
import { SectionCard } from '../../../pages/DriverProfile/components';
import { Row, SectionSkeleton, SectionError } from '../../../pages/UserProfile/parts';
import FleetApi from '../../../lib/api/FleetApi';

const FleetOperatorCard = ({ user }) => {
  const [approved, setApproved] = useState(false);
  const [assignedBy, setAssignedBy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // `try/finally` with no `catch`: a failed lookup left `approved` at its initial false,
  // so the card stated "Not approved for fleet opportunities" as a fact when what actually
  // happened is that nobody knows. That is the wrong answer to give someone on a call.
  const [failed, setFailed] = useState(false);
  // Bumped on every new request AND by the effect's cleanup, so one counter covers both
  // things the old `cancelled` flag did: it drops a response that arrives after unmount,
  // and it drops the previous driver's response when `user.id` changed mid-flight.
  const request = useRef(0);

  const load = useCallback(async () => {
    const seq = (request.current += 1);
    setLoading(true);
    setFailed(false);
    try {
      const data = await FleetApi.getDriverApproval(user.id);
      if (seq !== request.current) return;
      setApproved(!!data?.approved);
      setAssignedBy(data?.assignedBy ?? null);
    } catch {
      if (seq === request.current) setFailed(true);
    } finally {
      if (seq === request.current) setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    load();
    return () => {
      request.current += 1;
    };
  }, [load]);

  const handleToggle = async (e) => {
    const next = e.target.checked;
    setSaving(true);
    try {
      if (next) {
        const row = await FleetApi.approveDriver({ userId: user.id });
        setApproved(true);
        setAssignedBy(row?.assignedBy ?? null);
      } else {
        await FleetApi.revokeDriver({ userId: user.id });
        setApproved(false);
      }
    } catch (err) {
      alert(`Failed to update fleet approval: ${err?.message ?? err}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      title="Fleet operator"
      icon={<CheckCircleIcon sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />}
    >
      {loading && <SectionSkeleton rows={1} />}
      {!loading && failed && <SectionError onRetry={load}>Couldn&rsquo;t check fleet approval.</SectionError>}
      {!loading && !failed && (
        <Row
          first
          label={approved ? 'Approved for fleet opportunities' : 'Not approved for fleet opportunities'}
          // Who granted it, which is the only audit trail this toggle has.
          meta={approved && assignedBy ? `by ${assignedBy}` : null}
          // Boxed, because `Row` aligns its children on the text baseline and a Switch has
          // none — unwrapped it hangs below the label it belongs to.
          right={
            <Box sx={{ alignSelf: 'center', flexShrink: 0 }}>
              {/* Two things the theme forces, both learned the hard way:
                  - No `size="small"`. `theme.js:154` already sizes every Switch here to a
                    26×16 pill, and MUI's own `sizeSmall` styles are a more specific
                    selector than the theme's `thumb` slot — so asking for small gave a
                    16px thumb with a checked `translateX(16px)` inside a 26px track, and
                    it pushed straight out through the card's clipped edge.
                  - `color="success"`, not the default `primary`. In dark mode
                    `primary.main` is #FFFFFF and the theme pins the thumb to #fff, so an
                    ON switch was a white thumb on a white track. Green is what every
                    other "this is good" signal on the page uses. */}
              <Switch color="success" checked={approved} onChange={handleToggle} disabled={loading || saving} />
            </Box>
          }
        />
      )}
    </SectionCard>
  );
};

export default FleetOperatorCard;
